import { Country } from "@/types/Country";
import { Customer } from "@/types/FinderLocation";
import { Experience } from "@/utils/experience";

const isAllUpperCase = (string: string): boolean => {
  return string === string.toUpperCase();
};

export const stringToTitleCase = (string: string): string => {
  return string.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

export const titleCaseWithExclusions = (name: string, exclusions: Set<string>): string => {
  const words = name.split(" ");
  const formattedWords = words.map((word) => {
    if (exclusions.has(word.toUpperCase())) {
      return word.toUpperCase();
    }
    if (isAllUpperCase(word)) {
      return stringToTitleCase(word);
    }
    return word;
  });
  return formattedWords.join(" ");
};

export const removeSpecialCharacters = (address: string): string => {
  return address.replace(/[^\w\s#]/gi, "");
};

// Set of pharmacy names to exclude from title casing
export const pharmacyNamesToExclude = new Set(["CVS", "QFC"]);

export const customerWebsite = ({
  customer,
  language
}: {
  customer?: Customer;
  language: Experience["language"] | undefined;
}) => {
  if (!customer) {
    return;
  }
  if (!language) {
    return customer.website;
  }
  return customer.localizedWebsites?.[language] || customer.website;
};

export function getFinderCountryConfig(country?: Country | string): {
  countryName: string;
  numInitialResults: number;
  radius: number;
  size: number;
} {
  // All countries fetch 30 results. JP finder shows these by default, US/CA finder shows 10 initially & then will use the "show more" button to show up to 30 results
  const size = 30;

  // JP and CA search radii defaults to 50km and US search radius defaults to 25 miles (40233.6 meters)
  let radius = 0;
  switch (country) {
    case "jp":
      radius = 50000;
      break;
    case "ca":
      radius = 50000;
      break;
    case "us":
      radius = 40233.6;
      break;
    default:
      radius = 0;
  }
  let numInitialResults = 10;
  switch (country) {
    case "us":
      numInitialResults = 10;
      break;
    case "ca":
      numInitialResults = 10;
      break;
    case "jp":
      numInitialResults = 100;
      break;
    default:
      numInitialResults = 10;
      break;
  }

  return {
    countryName: country?.toUpperCase() || "INVALID",
    numInitialResults,
    radius,
    size
  };
}

export const getValidAgeString = (age: string): string => {
  const onlyDigitAge = age.replace(/\D/g, "").slice(0, 3);

  const ageNum = parseInt(onlyDigitAge, 10);
  if (isNaN(ageNum)) {
    return "";
  }
  if (ageNum > 130) {
    return onlyDigitAge.slice(0, 2);
  }
  return onlyDigitAge;
};

export * from "./detectUserLocation";
export * from "./googleMaps";
export * from "./reverseGeocode";
